import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  DataTable,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  FormattedDateTime,
  FormattedRelativeTime,
  InlineStack,
  Label,
  Link,
  Paragraph,
  Select,
  Stack,
  Text,
  TextField,
  Tooltip,
  type TooltipProps,
} from '@customerio/pluma-components/react';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { ConnectionStatus, ConnectionStatusText, DataFlow } from '../../catalog/enums';
import { IntegrationIcon } from '../../directory/components/integration-icon';
import { IntegrationStatus } from '../../directory/components/integration-status';
import { ProductApp, type IntegrationConnection } from '../view-model/types';
import { GroupConnections } from './group-connections';
import * as styles from './connections-table.css';
import {
  ConnectionColumn,
  DEFAULT_SORTING,
  FILTER_BREAKPOINT,
  filterAndSortConnections,
  getTypeName,
  groupConnections,
  type ConnectionSorting,
} from './table-model';

const NAME_TOOLTIP_THRESHOLD = 31;

const STATUS_OPTIONS = [
  { label: 'All statuses', value: 'All' },
  ...Object.entries(ConnectionStatusText)
    .filter(([value]) => value !== ConnectionStatus.multiple)
    .map(([value, label]) => ({ label, value })),
];

const DATA_FLOW_OPTIONS = [
  { label: 'All connections', value: 'All' },
  { label: 'Data in', value: DataFlow.in },
  { label: 'Data out', value: DataFlow.out },
  { label: 'Channel', value: DataFlow.channel },
];

/* --------------------------------- cells --------------------------------- */

type CellProps = { connection: IntegrationConnection };

function ConnectionDataFlowCell({ connection }: CellProps) {
  const { dataFlow } = connection;
  if (dataFlow == null || ![DataFlow.in, DataFlow.out, DataFlow.channel].includes(dataFlow)) {
    return null;
  }
  const isDataIn = dataFlow === DataFlow.in;
  const isChannel = dataFlow === DataFlow.channel;
  const color = isDataIn ? 'teal' : isChannel ? 'blue' : 'plum';
  const label = isChannel ? 'Channel' : isDataIn ? 'Data in' : 'Data out';
  return (
    <Label color={color} bold data-test-integrations-connections-card-data-flow={isChannel ? 'channel' : dataFlow}>
      {label}
    </Label>
  );
}

type ConnectionTitleCellProps = CellProps & { workspaceId: string };

/**
 * The `reference` slot of Pluma's custom-tooltip render context, derived from
 * `TooltipProps` so it tracks the component instead of restating it. The reference is
 * attached to an element that already exists, so the tooltip adds no wrapper of its own
 * (see `ConnectionTitleCell`).
 */
type TooltipRenderContext = Parameters<Extract<TooltipProps['children'], (context: never) => unknown>>[0];

type ConnectionTitleLinkProps = CellProps & { tooltip: TooltipRenderContext['reference'] };

function ConnectionTitleLink({ connection, tooltip }: ConnectionTitleLinkProps) {
  if (connection.href != null) {
    return (
      <Link
        ref={(node) => {
          tooltip.setReference(node);
        }}
        {...tooltip.getReferenceProps()}
        href={connection.href}
        isExternal={connection.product !== ProductApp.journeys}
        variant="primary"
        data-test-integrations-connections-card-title
      >
        {connection.name}
      </Link>
    );
  }
  // Ember attaches `{{tt.setReference}}` in its link branch only, so a non-link title
  // (a grouped row) never gets a tooltip there. Not carried over: a group's name is
  // `getTypeName`, which can exceed the threshold — `Amazon Simple Storage Service / S3`
  // is 34 characters — and those rows truncate like any other, so Ember leaves no way to
  // read the full name. Attaching the reference here too also keeps the tooltip from
  // being enabled with nothing to anchor it to.
  return (
    <Text
      ref={(node) => {
        tooltip.setReference(node);
      }}
      {...tooltip.getReferenceProps()}
      weight="semibold"
      color="active"
      data-test-integrations-connections-card-title
    >
      {connection.name}
    </Text>
  );
}

type ConnectionTitleSubTextProps = {
  connection: IntegrationConnection;
  typeName: string;
  shouldShowTypeName: boolean;
  workspaceId: string;
};

function ConnectionTitleSubText({
  connection,
  typeName,
  shouldShowTypeName,
  workspaceId,
}: ConnectionTitleSubTextProps) {
  const isAutoTrackGroup =
    connection.href == null && connection.type === 'customerio' && connection.isAutoTrack === true;
  if (isAutoTrackGroup) {
    return (
      <Link
        isUnderlined
        variant="secondary"
        icon="manage"
        // The API credentials page is Hydra-owned (FPS-2224); hand off via a raw href.
        // oxlint-disable-next-line local-rules/enforce-generate-path -- Hydra API credentials hand-off, not a Journeys router path
        href={`/workspaces/${workspaceId}/settings/api_credentials`}
        data-test-integrations-connections-card-title-sub-text
      >
        Manage credentials
      </Link>
    );
  }
  if (shouldShowTypeName) {
    return (
      <Paragraph variant="secondary" data-test-integrations-connections-card-title-sub-text>
        {typeName}
      </Paragraph>
    );
  }
  return null;
}

function ConnectionTitleCell({ connection, workspaceId }: ConnectionTitleCellProps) {
  const hasChildren = connection.href == null && connection.children != null && connection.children.length > 0;
  const typeName = hasChildren ? `${connection.children?.length ?? 0} connections` : getTypeName(connection);
  const shouldShowTypeName = connection.name !== typeName;

  // `isCustom` is what keeps the name truncating. The default form wraps its children
  // in a `<Box as="button">`, and that button is a max-content-width flex child of the
  // cell — so the `w="full"` below resolved against the button's own content width, the
  // `truncate` box never had a narrower box to clamp to, and long names were hard-clipped
  // by the cell with no ellipsis (only names past NAME_TOOLTIP_THRESHOLD, which is why
  // truncation looked intermittent). Ember passes `@isCustom={{true}}` and attaches the
  // reference to the link, adding no element; this does the same. It also avoids nesting
  // a button around the row's link.
  return (
    <Tooltip
      placement="top-start"
      isCustom
      content={connection.name}
      isDisabled={connection.name.length < NAME_TOOLTIP_THRESHOLD}
    >
      {(context) => (
        <InlineStack
          alignInline="flex-start"
          alignBlock={shouldShowTypeName ? 'flex-start' : 'center'}
          gap="100"
          w="full"
        >
          <Stack alignBlock="center" flexShrink="0" flexGrow="0" pt={shouldShowTypeName ? '050' : undefined}>
            <IntegrationIcon integration={connection} size="sm" />
          </Stack>
          <Stack>
            <Box truncate>
              <ConnectionTitleLink connection={connection} tooltip={context.reference} />
            </Box>
            <ConnectionTitleSubText
              connection={connection}
              typeName={typeName}
              shouldShowTypeName={shouldShowTypeName}
              workspaceId={workspaceId}
            />
          </Stack>
        </InlineStack>
      )}
    </Tooltip>
  );
}

function ConnectionStatusCell({ connection }: CellProps) {
  if (connection.status == null) {
    return null;
  }
  return <IntegrationStatus status={connection.status} description={connection.statusMessage} />;
}

function ConnectionDateAddedCell({ connection }: CellProps) {
  if ('children' in connection) {
    return null;
  }
  const { createdAt } = connection;
  if (createdAt == null || createdAt <= 0) {
    return null;
  }
  return <FormattedDateTime data-test-connection-info="Date added" date={createdAt * 1000} format="medium" />;
}

function ConnectionLastActivityCell({ connection }: CellProps) {
  if ('children' in connection) {
    return null;
  }
  const { lastActivity } = connection;
  if (lastActivity != null && lastActivity > 0) {
    return <FormattedRelativeTime data-test-connection-info="Last activity" date={lastActivity * 1000} />;
  }
  if (connection.dataFlow !== DataFlow.channel) {
    return (
      <Tooltip placement="top" content="No activity has ever been recorded for this integration">
        <Text as="span" data-test-connection-info="Last activity">
          &#8212;
        </Text>
      </Tooltip>
    );
  }
  return <span data-test-connection-info="Last activity">&#8212;</span>;
}

/** Navigate to the cross-app Pipelines add flow for a pipelines connection. */
function addRelatedConnectionUrl(connection: IntegrationConnection): string | undefined {
  if (connection.href == null) {
    return undefined;
  }
  const type = connection.dataFlow === DataFlow.out ? 'source' : 'destination';
  return `${connection.href}?action=add_${type}`;
}

/* --------------------------------- table --------------------------------- */

type ConnectionsTableProps = {
  connections: IntegrationConnection[];
  isSearchable?: boolean;
  /** Accepted for parity with the Ember component signature (currently unrendered, as in Ember). */
  addConnectionButtonHref?: string;
  addDataInConnectionButtonHref?: string;
  addDataOutConnectionButtonHref?: string;
  /** `cdp_track_enabled` OR `cdp_passthrough_enabled` — controls auto-track grouping. */
  shouldGroupAutoTrack: boolean;
  /**
   * Every Pipelines connection keyed by wire id, so the Connections column can
   * resolve the `connectedTo` ids on the other side of each flow (Ember's
   * `store.peekRecord`). Omitted, the column shows only the add-connection button.
   */
  pipelinesConnectionsById?: ReadonlyMap<string, IntegrationConnection>;
  workspaceId: string;
};

export function ConnectionsTable({
  connections,
  isSearchable,
  shouldGroupAutoTrack,
  pipelinesConnectionsById,
  workspaceId,
}: ConnectionsTableProps) {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConnectionStatus | 'All'>('All');
  const [dataFlowFilter, setDataFlowFilter] = useState<DataFlow | 'All'>('All');
  const [sorting, setSorting] = useState<ConnectionSorting>(DEFAULT_SORTING);

  const shouldShowFilters = isSearchable === true && connections.length > FILTER_BREAKPOINT;

  const groupedConnections = useMemo(() => {
    const filtered = filterAndSortConnections(connections, { searchText, statusFilter, dataFlowFilter }, sorting);
    return groupConnections(filtered, shouldGroupAutoTrack);
  }, [connections, searchText, statusFilter, dataFlowFilter, sorting, shouldGroupAutoTrack]);

  const resolveRelated = useCallback(
    (lookupId: string) => pipelinesConnectionsById?.get(lookupId) ?? null,
    [pipelinesConnectionsById],
  );

  const resetFilters = useCallback(() => {
    setSearchText('');
    setStatusFilter('All');
    setDataFlowFilter('All');
  }, []);

  const columns = useMemo<ColumnDef<IntegrationConnection>[]>(() => {
    const columnHelper = createColumnHelper<IntegrationConnection>();
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return [
      columnHelper.accessor(ConnectionColumn.type, {
        header: 'Type',
        meta: { width: 120, minWidth: 120 },
        cell: (info) => <ConnectionDataFlowCell connection={info.row.original} />,
      }),
      columnHelper.accessor(ConnectionColumn.name, {
        header: 'Name',
        meta: { width: '1fr', minWidth: 200 },
        cell: (info) => <ConnectionTitleCell connection={info.row.original} workspaceId={workspaceId} />,
      }),
      columnHelper.accessor(ConnectionColumn.status, {
        header: 'Status',
        meta: { width: 200 },
        cell: (info) => <ConnectionStatusCell connection={info.row.original} />,
      }),
      columnHelper.accessor(ConnectionColumn.connections, {
        header: 'Connections',
        meta: { width: 220 },
        enableSorting: false,
        cell: (info) => {
          const connection = info.row.original;
          const onClick =
            connection.product === ProductApp.journeys
              ? undefined
              : () => {
                  const url = addRelatedConnectionUrl(connection);
                  if (url != null) {
                    window.location.assign(url);
                  }
                };
          return <GroupConnections connection={connection} onClick={onClick} resolveRelated={resolveRelated} />;
        },
      }),
      columnHelper.accessor(ConnectionColumn.dateAdded, {
        header: 'Date added',
        meta: { width: 160 },
        cell: (info) => <ConnectionDateAddedCell connection={info.row.original} />,
      }),
      columnHelper.accessor(ConnectionColumn.lastActivity, {
        header: 'Last activity',
        meta: { width: 160 },
        cell: (info) => <ConnectionLastActivityCell connection={info.row.original} />,
      }),
    ] as ColumnDef<IntegrationConnection>[];
  }, [workspaceId, resolveRelated]);

  const emptyStateComponent = useCallback(
    () => (
      <EmptyState>
        <EmptyStateTitle icon="connections">No results found</EmptyStateTitle>
        <EmptyStateDescription>Try adjusting your filters to find matches.</EmptyStateDescription>
        <EmptyStateActions>
          <Button variant="primary" onClick={resetFilters}>
            Reset filters
          </Button>
        </EmptyStateActions>
      </EmptyState>
    ),
    [resetFilters],
  );

  // `isManual: false` is load-bearing, not decoration. Ember passes the deprecated
  // `@sorting` + `@onSortingChange` pair, which leaves `manualSorting` unset, so
  // TanStack sorts the *grouped* rows itself with `sortingFn: 'auto'` and
  // `sortUndefined: 1` — group rows sort by their group name/status/date, and rows
  // with no date land first when descending. Pluma's newer `{ value, onChange }`
  // shape defaults to `manualSorting: true`, which left Hydra rendering the
  // pre-grouping order from `filterAndSortConnections` (group rows sitting at their
  // first child's position, "Multiple" statuses interleaved with the errors they
  // were grouped from). Opting back out restores the Ember order.
  const tableSorting = useMemo(
    () => ({ value: sorting, onChange: (next: ConnectionSorting) => setSorting(next), isManual: false }),
    [sorting],
  );

  return (
    <Stack gap="200">
      {shouldShowFilters ? (
        <InlineStack gap="250" mt="150">
          <TextField
            data-test-integrations-connection-filter="search"
            icon="search"
            ariaLabel="Search connections by name"
            placeholder="Search by name"
            isClearable
            onClear={() => setSearchText('')}
            value={searchText}
            onInput={(event) =>
              setSearchText(
                // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
                (event.target as HTMLInputElement).value,
              )
            }
            flexGrow="1"
          />
          <Select
            data-test-integrations-connection-filter="dataFlow"
            ariaLabel="Filter by data flow"
            options={DATA_FLOW_OPTIONS}
            value={dataFlowFilter}
            onChange={(value) =>
              setDataFlowFilter(
                // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
                value as DataFlow | 'All',
              )
            }
            className={styles.statusFilter}
          />
          <Select
            data-test-integrations-connection-filter="status"
            ariaLabel="Filter by status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(value) =>
              setStatusFilter(
                // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
                value as ConnectionStatus | 'All',
              )
            }
            className={styles.statusFilter}
          />
        </InlineStack>
      ) : null}

      <DataTable
        caption="Connections"
        columns={columns}
        data={groupedConnections}
        withSorting
        sorting={tableSorting}
        withRowExpanding
        emptyStateComponent={emptyStateComponent}
      />
    </Stack>
  );
}
